import Occasion from '../helpers/Occasion';
import { OrderedMap, Set } from 'immutable';
import { ContactInfoType, ID, IDRecord, SearchSort } from '../types';
import Contact from './Contact';
import { itemListToString } from '../helpers/string';
import SearchResults from './SearchResults';
import { applySortStateToObj } from '../helpers/sorting';
import { comparatorForSort, mapAndSortContacts } from '../helpers/contact-sort';

export const sortGroupMembers = (
  group: Group,
  getContact: (id: ID) => Contact,
  sort: SearchSort,
): Group => {
  const comparator = comparatorForSort(sort, getContact);
  return group.copyWith({ items: group.items.sort(comparator) });
};

export type GroupReconnectCadence =
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'yearly'
  | 'disabled'
  | 'auto';

type GroupImport = {
  uuid: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
};

interface GroupData {
  id?: string;
  title?: string;
  created?: Occasion | string;
  color?: string;
  reconnect_cadence?: GroupReconnectCadence;
  is_internal?: boolean;
  slug?: string;
  items?: OrderedMap<ID, IDRecord>;
  isFetching?: boolean;
  persistedItemsOnServer?: number;
  memberIDs?: Set<ID>;
  sorting?: SearchSort;
  allContactsLoaded?: boolean;
  bulk_import?: GroupImport;
  // this comes in from the index GET
  contact_ids?: Array<ID>;
}

const displayPersonCount = (count: number) => {
  const text = count == 1 ? 'person' : 'people';
  return `${count} ${text}`;
};

type GroupDiff = {
  id: string;
  title: string;
  color: string;
  reconnect_cadence: GroupReconnectCadence;
  add_contact_ids: ID[];
  remove_contact_ids: ID[];
};

export interface BulkGroupUpdate {
  contact_ids: ID[];
  add_group_ids: ID[];
  remove_group_ids: ID[];
  create_groups: string[];
}

type ContactIsRestrictedFn = (contact: Contact) => boolean;

export type GroupColorHex = string;
export type GroupColorLabel = string;

export const groupColors: [GroupColorHex, GroupColorLabel][] = [
  ['#ffb74a', 'yellow'],
  ['#ff7e44', 'orange'],
  ['#eb515b', 'cherry'],
  ['#df5d86', 'pink'],
  ['#ae98c1', 'lilac'],
  ['#7a8bb5', 'blueberry'],

  ['#4498bd', 'sky blue'],
  ['#0072af', 'ocean blue'],
  ['#79a0aa', 'steel blue'],
  ['#57aeb2', 'turquoise'],
  ['#6eaf62', 'plant green'],
  ['#349d63', 'green'],

  // ['#F2F2F2', 'white lily'],
  ['#8F8F8F', 'mouse gray'],
  ['#656565', 'granite'],
  ['#4A4A4A', 'gunpowder'],
  // ['#262626', 'charcoal'],
  // ['#1a1a1a', 'ebony'],
];

export const legacyHexToNewHex: (hex: GroupColorHex) => GroupColorHex = (hex) => {
  if (!hex) {
    return '#8F8F8F';
  }

  switch (hex.toLowerCase()) {
    case '#f2f2f2':
      return '#8F8F8F';
    case '#262626':
      return '#656565';
    case '#1a1a1a':
      return '#4A4A4A';
  }

  return hex;
};

export const groupPageLimit = 1000;

export class BaseGroup extends SearchResults {
  title: string;
  created: Occasion;
  color: string;
  slug?: string;
  // this is total membership. `items` on the group will be paginated separately.
  memberIDs: Set<ID>;
  is_internal: boolean;
  reconnect_cadence: GroupReconnectCadence;
  bulk_import?: GroupImport;
  sorting: SearchSort;
  allContactsLoaded: boolean = false;
  contactIsRestricted: ContactIsRestrictedFn = () => false;
  getContact: (id: ID) => Contact | undefined;

  constructor(props: GroupData = {}) {
    super(props);

    // @ts-ignore: this is hack for different activity feed data
    this.title = props.title || props.name || '';
    this.created = new Occasion(props.created);
    this.color = legacyHexToNewHex(props.color);
    this.reconnect_cadence = props.reconnect_cadence || 'auto';
    this.is_internal = !!props.slug; //props.is_internal || false;
    this.slug = props.slug;
    this.limit = groupPageLimit;
    this.persistedItemsOnServer = props.persistedItemsOnServer || -1;
    this.memberIDs = props.memberIDs || Set();
    this.sorting = props.sorting || applySortStateToObj({}, 'lastName');
    this.allContactsLoaded = props.allContactsLoaded || false;
    this.bulk_import = props.bulk_import;
  }

  get totalResults(): number {
    return (
      this.memberIDs.size ||
      (this.persistedItemsOnServer > 0 && this.persistedItemsOnServer) ||
      this.items.size
    );
  }

  get count() {
    return this.totalResults;
  }

  get filteredItems() {
    return this.items;
  }

  get canLoadMore() {
    return this.persistedItemsOnServer > this.items.size;
  }

  get importStatus() {
    return this.bulk_import?.status;
  }

  get isImporting() {
    return this.importStatus && ['pending', 'processing'].includes(this.importStatus);
  }

  hasContact(contactID: ID): boolean {
    return this.memberIDs?.has(contactID);
  }

  hasContactGroup(contactGroup: Array<Contact>): boolean {
    return contactGroup.every(({ id }) => this.memberIDs.has(id));
  }

  hasAllContactIDs(contactIDs: ID[]): boolean {
    return contactIDs.every((id) => this.memberIDs.has(id));
  }

  removeContact(id: ID): BaseGroup {
    return this.copyWith({
      items: this.items.delete(id),
      memberIDs: this.memberIDs.delete(id),
      persistedItemsOnServer: this.persistedItemsOnServer - 1,
    });
  }

  removeContactGroup(contactGroup: Array<Contact>): BaseGroup {
    return this.removeContactIDSet(Set(contactGroup.map(({ id }) => id)));
  }

  removeContactByID(id: ID): BaseGroup {
    return this.copyWith({
      items: this.items.delete(String(id)),
      memberIDs: this.memberIDs.delete(id),
      persistedItemsOnServer: this.persistedItemsOnServer - 1,
    });
  }

  removeContactGroupByIDs(ids: Array<ID>): BaseGroup {
    return this.removeContactIDSet(Set(ids));
  }

  removeContactIDSet(idSet: Set<ID>): BaseGroup {
    const idsToRemove = idSet.intersect(this.memberIDs);
    return this.copyWith({
      items: this.items.filter((_, id) => !idSet.has(id)),
      memberIDs: this.memberIDs.subtract(idSet),
      persistedItemsOnServer: this.persistedItemsOnServer - idsToRemove.size,
    });
  }

  addContact(id: ID): BaseGroup {
    return this.copyWith({
      items: OrderedMap([[id, { id }]]).merge(this.items),
      memberIDs: this.memberIDs.add(id),
    });
  }

  addContactGroup(contactGroup: Array<Contact>): BaseGroup {
    const contactGroupMap = OrderedMap(
      contactGroup.map((contact) => {
        const id = String(contact.id);
        return [String(id), { id }];
      }),
    );
    return this.copyWith({
      items: contactGroupMap.merge(this.items),
      memberIDs: this.memberIDs.union(contactGroup.map(({ id }) => id)),
    });
  }

  addContactIDs(ids: ID[]): BaseGroup {
    return this.copyWith({
      items: OrderedMap(ids.map((id) => [id, { id }])).merge(this.items),
      memberIDs: this.memberIDs.union(ids),
    });
  }

  handleMergedContact(mergedIntoID: ID, mergedContactIDs: ID[]): BaseGroup {
    let mergedIDs = Set(mergedContactIDs);

    // if the group has none of the mergedIDs, just return the group
    if (!mergedIDs.intersect(this.memberIDs).size) {
      return this;
    }

    if (mergedIntoID) {
      mergedIDs = mergedIDs.remove(mergedIntoID);
    }

    const groupMinusMerged = this.removeContactIDSet(mergedIDs);
    return groupMinusMerged.addContact(mergedIntoID).copyWith({ updated: new Occasion() });
  }

  getContactInfoOfType(
    contactInfoType: ContactInfoType,
    separator: ',' | '\n',
    selectedIDs?: Immutable.Set<ID>,
  ): string {
    let contactIDs = this.memberIDs;

    if (selectedIDs) {
      contactIDs = contactIDs.union(selectedIDs);
    }

    return mapAndSortContacts(contactIDs, this.getContact, this.sorting)
      .filter((contact) => !!contact && !this.contactIsRestricted(contact))
      .map((contact) => contact.getContactInfoOfType(contactInfoType))
      .filter((contactInfo) => contactInfo != null)
      .join(separator);
  }

  private copyEmailsToClipboard(selectedIDs?: Immutable.Set<ID>) {
    return () => {
      const emails = this.getContactInfoOfType('email', ',', selectedIDs);
      if (emails.length > 0) {
        navigator.clipboard.writeText(emails);
      }
    };
  }

  getAllEmails(
    contactIsRestricted: ContactIsRestrictedFn,
    getContact: (id: ID) => Contact,
  ): string {
    this.contactIsRestricted = contactIsRestricted;
    this.getContact = getContact;
    return this.getContactInfoOfType('email', ',', undefined);
  }

  copyAllEmails(contactIsRestricted: ContactIsRestrictedFn, getContact: (id: ID) => Contact) {
    this.contactIsRestricted = contactIsRestricted;
    this.getContact = getContact;
    return this.copyEmailsToClipboard();
  }

  hasContactInfoOfType(
    contactInfoType: ContactInfoType,
    contactIsRestricted: ContactIsRestrictedFn,
    getContact: (id: ID) => Contact,
  ): boolean {
    this.contactIsRestricted = contactIsRestricted;
    this.getContact = getContact;

    return this.memberIDs
      .map((id) => getContact(id))
      .filter((contact) => !!contact && !this.contactIsRestricted(contact))
      .some((contact) => !!contact.getContactInfoOfType(contactInfoType));
  }

  private copyPhoneNumbersToClipboard(selectedIDs?: Immutable.Set<ID>) {
    return () => {
      const phoneNumbers = this.getContactInfoOfType('phone', '\n', selectedIDs);
      if (phoneNumbers.length > 0) {
        navigator.clipboard.writeText(phoneNumbers);
      }
    };
  }

  getAllPhoneNumbers(
    contactIsRestricted: ContactIsRestrictedFn,
    getContact: (id: ID) => Contact,
  ): string {
    this.contactIsRestricted = contactIsRestricted;
    this.getContact = getContact;
    return this.getContactInfoOfType('phone', '\n', undefined);
  }

  copyAllPhoneNumbers(contactIsRestricted: ContactIsRestrictedFn, getContact: (id: ID) => Contact) {
    this.contactIsRestricted = contactIsRestricted;
    this.getContact = getContact;
    return this.copyPhoneNumbersToClipboard();
  }

  toggleContactInclusion(contactID: ID): BaseGroup {
    if (this.hasContact(contactID)) {
      return this.removeContact(contactID);
    }
    return this.addContact(contactID);
  }

  diff(editedGroup: Group): GroupDiff {
    const addedContactIDs = editedGroup.memberIDs.subtract(this.memberIDs);
    const removedContactIDs = this.memberIDs.subtract(editedGroup.memberIDs);

    return {
      id: editedGroup.id,
      title: editedGroup.title,
      color: editedGroup.color,
      reconnect_cadence: editedGroup.reconnect_cadence,
      add_contact_ids: addedContactIDs.toArray(),
      remove_contact_ids: removedContactIDs.toArray(),
    };
  }

  diffText(editedGroup: Group): string {
    const diff = this.diff(editedGroup);
    const diffParts = [];

    if (diff.title != this.title) {
      diffParts.push(`changed the title from "${this.title}" to "${editedGroup.title}"`);
    }

    if (diff.color != this.color) {
      diffParts.push('changed the highlight color');
    }

    if (diff.reconnect_cadence != this.reconnect_cadence) {
      diffParts.push(`changed the reconnect cadence to ${editedGroup.reconnect_cadence}`);
    }

    if (diff.add_contact_ids.length > 0) {
      diffParts.push(`added ${displayPersonCount(diff.add_contact_ids.length)}`);
    }

    if (diff.remove_contact_ids.length > 0) {
      diffParts.push(`removed ${displayPersonCount(diff.remove_contact_ids.length)}`);
    }

    return itemListToString(diffParts);
  }

  isDifferent(editedGroup: Group): boolean {
    const diff = this.diff(editedGroup);
    return (
      this.title != diff.title ||
      this.color != diff.color ||
      this.reconnect_cadence != diff.reconnect_cadence ||
      diff.add_contact_ids.length > 0 ||
      diff.remove_contact_ids.length > 0
    );
  }
}

export default class Group extends BaseGroup {
  constructor(props: GroupData = {}) {
    super(props);

    if (props.contact_ids) {
      this.memberIDs = Set(props.contact_ids.map(String));
    }
  }
}
